import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { projectsApi, Project } from '../services/api'

interface ProjectContextType {
  currentProject: Project | null
  setCurrentProject: (project: Project | null) => void
  projects: Project[]
  isLoading: boolean
  error: Error | null
  refreshProjects: () => void
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined)

const STORAGE_KEY = 'lextool_current_project_id'

// Note: This provider should only be used inside OnboardingGuard,
// which ensures the user is fully authenticated and onboarded.
export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const [currentProject, setCurrentProjectState] = useState<Project | null>(null)
  const queryClient = useQueryClient()

  const {
    data: projects = [],
    isLoading,
    error,
    refetch: refreshProjects,
  } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list().then(r => r.data),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Load saved project from localStorage on mount
  useEffect(() => {
    const savedProjectId = localStorage.getItem(STORAGE_KEY)
    if (savedProjectId && projects.length > 0) {
      const saved = projects.find(p => p.id === savedProjectId)
      if (saved) {
        setCurrentProjectState(saved)
      } else if (projects.length > 0) {
        // If saved project not found, use first project
        setCurrentProjectState(projects[0])
      }
    } else if (projects.length > 0 && !currentProject) {
      // Default to first project if none selected
      setCurrentProjectState(projects[0])
    }
  }, [projects])

  const setCurrentProject = useCallback((project: Project | null) => {
    setCurrentProjectState(project)
    if (project) {
      localStorage.setItem(STORAGE_KEY, project.id)
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
    // Invalidate queries that depend on project
    queryClient.invalidateQueries({ queryKey: ['documents'] })
  }, [queryClient])

  const value: ProjectContextType = {
    currentProject,
    setCurrentProject,
    projects,
    isLoading,
    error: error as Error | null,
    refreshProjects: () => refreshProjects(),
  }

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  )
}

export function useProject() {
  const context = useContext(ProjectContext)
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider')
  }
  return context
}
